import { GenericService } from "../generic-service";
import type { IMetaParams } from "../templates-wpp-meta/meta-wpp-template.service";

export enum TipoBotAcao {
  TEXTO = 'TEXTO',
  MIDIA = 'MIDIA',
  LISTA_DE_OPCOES = 'LISTA_DE_OPCOES',
  LISTA_DE_CANAIS = 'LISTA_DE_CANAIS',
  AGUARDAR = 'AGUARDAR',
  FINALIZAR = 'FINALIZAR',
  TRANSFERIR = 'TRANSFERIR',
  LISTA_DE_AVALIACAO = 'LISTA_DE_AVALIACAO',
  REQUISICAO_HTTPS = 'REQUISICAO_HTTPS',
  ENVIAR_TEMPLATE = 'ENVIAR_TEMPLATE',
  ACAO_AXION = 'ACAO_AXION',
  ATIVAR_AGENTE_IA = 'ATIVAR_AGENTE_IA'
}

/** `WHIZ` = template interno; `META` = template oficial do inbox do bot. */
export type ConteudoBotAcaoEnviarTemplate = {
  origem: 'WHIZ' | 'META';
  idTemplate: string;
  /** Só para `META`. Valores aceitam as variáveis do bot (`{nome_contato}` etc.). */
  metaTemplateParams?: IMetaParams;
  /** Apenas para exibição no card do editor. */
  nomeTemplate?: string;
}

export type ConteudoBotAcaoTexto = {
  texto: string;
  aguardarResposta?: boolean;
}

export type ConteudoBotAcaoMidia = {
  url: string;
  legenda?: string;
  nomeArquivo?: string;
  tamanho?: number;
  tipo?: string;
  toUpload?: boolean;
  file?: File;
  aguardarResposta?: boolean;
  /** Tempo da saída por tempo ("sem resposta"); a transição guarda o valor que vale. */
  minutosAguardar?: number;
}

export type ConteudoBotAcaoListaDeOpcoes = {
  mensagem: string;
  lista: {
    titulo: string;
    opcoes: {
      titulo: string,
      descricao: string,
      id: string
    }[]
  };
  forcarResposta?: boolean; // ficará reenviando a lista até o cliente escolher uma opção
}

export type ConteudoBotAcaoListaDeCanais = {
  mensagem?: string;
  mensagemRedirecionamento?: string;
  idsCanais: string[];
  forcarResposta?: boolean; // ficará reenviando a lista até o cliente escolher uma opção
  continuacoes: {
    idCanal: string;
  }[];
}

export type ConteudoBotAcaoFinalizar = {
  mensagem?: string;
}

export type ConteudoBotAcaoTransferir = {
  idCanal?: string;
  idAtendente?: string;
  mensagemRedirecionamento?: string;
}

export type ConteudoBotAcaoAguardar = {
  minutos?: number;
}

export type MetodoRequisicaoHttps = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type FormatoBodyRequisicaoHttps = 'JSON' | 'FORM_URLENCODED' | 'NENHUM';

export type TipoAutorizacaoRequisicaoHttps = 'NENHUMA' | 'API_KEY' | 'HEADER';

/**
 * Credencial write-only: o backend não devolve `valor`, só `temValor`. Enviar sem
 * `valor` mantém o que está gravado; `tipo: 'NENHUMA'` remove.
 */
export type AutorizacaoRequisicaoHttps = {
  tipo: TipoAutorizacaoRequisicaoHttps;
  chave?: string;
  valor?: string;
  temValor?: boolean;
}

export type ConteudoBotAcaoRequisicaoHttps = {
  url: string;
  metodo?: MetodoRequisicaoHttps;
  formatoBody?: FormatoBodyRequisicaoHttps;
  bodyModo?: 'PARES' | 'CRU';
  autorizacao?: AutorizacaoRequisicaoHttps;
  headers?: Record<string, string>;
  body?: string;
  /** @deprecated Substituído por `autorizacao`. Mantido para nós antigos. */
  apiKey?: string;
}

export type ConteudoBotAcaoListaDeAvaliacao = {
  mensagem?: string;
  lista: {
    titulo: string;
    opcoes: {
      titulo: string,
      descricao?: string,
      id: number
    }[]
  };
  forcarResposta?: boolean; // ficará reenviando a lista até o cliente escolher uma opção
}

/**
 * Nó "Ações Axion": age nos cards do CRM do Axion pelo telefone do contato. Os nomes/cor
 * só servem para desenhar o nó; o servidor usa os ids. Origem ausente = todos os cards do
 * telefone; só `idFunil` = qualquer etapa daquele funil.
 */
export type AcaoCrmAxion = 'EXCLUIR_CARD' | 'MOVER_ETAPA' | 'MUDAR_MARCADOR';

export type ConteudoBotAcaoAxion = {
  modulo: 'CRM';
  acao: AcaoCrmAxion;
  origem?: { idFunil?: string | null; idEtapa?: string | null; nomeFunil?: string | null; nomeEtapa?: string | null } | null;
  destino?: { idFunil: string; idEtapa: string; nomeFunil?: string | null; nomeEtapa?: string | null } | null;
  marcador?: { id: string; nome?: string | null; cor?: string | null } | null;
}

export type ConteudoBotAcao = ConteudoBotAcaoTexto | ConteudoBotAcaoMidia | ConteudoBotAcaoListaDeOpcoes |
  ConteudoBotAcaoListaDeCanais | ConteudoBotAcaoListaDeAvaliacao | ConteudoBotAcaoAguardar |
  ConteudoBotAcaoTransferir | ConteudoBotAcaoFinalizar | ConteudoBotAcaoRequisicaoHttps |
  ConteudoBotAcaoEnviarTemplate | ConteudoBotAcaoAxion;

export type CreateBotAcao = {
  idTemporario: string;
  tipo: TipoBotAcao;
  conteudo: ConteudoBotAcao;
  x?: number;
  y?: number;
  incial?: boolean;
  /** Id devolvido pelo GET, quando a ação já existia. Permite ao backend herdar credenciais não reenviadas. */
  idOriginal?: string;
}

export type ResponseBotAcao = {
  id: string;
  tipo: TipoBotAcao;
  conteudo: ConteudoBotAcao;
  x?: number;
  y?: number;
  idBot: string;
  inicial: boolean;
};

export type BotAcaoTransicao = {
  acaoOrigem: string;
  acaoDestino: string;
  valor?: string;
  minutosAguardar?: string;
}

export type ResponseBotAcaoTransicao = {
  id: string;
  origem: string;
  destino: string;
  valor?: string;
  minutosAguardar?: string;
}

export type CreateBotsAcoes = {
  idBot: string;
  acoes: CreateBotAcao[];
  transicoes: BotAcaoTransicao[];
}

export type ResponseBotsAcoes = {
  idBot: string;
  acoes: ResponseBotAcao[];
  transicoes: ResponseBotAcaoTransicao[];
}

export class BotsAcoesService extends GenericService<CreateBotsAcoes, { id: string }, object> {
  constructor() {
    super("bots-acoes");
  }

  async getAcoesETransicoes(idBot: string): Promise<ResponseBotsAcoes> {
    return (await this.f.get('', { idBot })) as ResponseBotsAcoes;
  }
}
